import { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle
} from '@/components/ui/drawer'
import { useI18n } from '@/i18n/i18n-react'
import { StoredBooleanSubject } from '@/utils/storage'
import { useGetSubject } from '@/utils/hooks'
import { BehaviorSubject } from 'rxjs'

interface OnboardingDrawerProps {
	open: boolean
	onOpenChange: (open: boolean) => void
}

export const onboardingCompleteSubject = new StoredBooleanSubject('onboarding-complete', false)

const OnboardingDrawerComponent = ({ open, onOpenChange }: OnboardingDrawerProps) => {
	const { L } = useI18n()
	const [currentStep, setCurrentStep] = useState(0)

	const steps = useMemo(
		() => [
			{
				title: L.Onboarding.FirstStep.Title(),
				description: L.Onboarding.FirstStep.Description()
			},
			{
				title: L.Onboarding.SecondStep.Title(),
				description: L.Onboarding.SecondStep.Description()
			},
			{
				title: L.Onboarding.ThirdStep.Title(),
				description: L.Onboarding.ThirdStep.Description()
			}
		],
		[L]
	)

	const handleNext = () => {
		if (currentStep < steps.length - 1) {
			setCurrentStep(currentStep + 1)
		} else {
			onboardingCompleteSubject.next(true)
			onOpenChange(false)
			setCurrentStep(0)
		}
	}

	const handleBack = () => {
		if (currentStep > 0) {
			setCurrentStep(currentStep - 1)
		}
	}

	const stepContent = steps[currentStep]
	const isLastStep = currentStep === steps.length - 1

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className='mx-auto max-w-md'>
				<DrawerHeader className='text-center'>
					<DrawerTitle className='text-xl font-bold'>{stepContent.title}</DrawerTitle>
					<DrawerDescription className='mt-2 text-base'>{stepContent.description}</DrawerDescription>

					<div className='mt-4 flex justify-center space-x-2'>
						{steps.map((_, index) => (
							<div
								key={index}
								className={`h-2 w-2 rounded-full transition-colors ${
									index === currentStep ? 'bg-primary'
									: index < currentStep ? 'bg-primary/50'
									: 'bg-muted'
								}`}
							/>
						))}
					</div>
				</DrawerHeader>

				<DrawerFooter className='flex-row justify-between'>
					<Button variant='outline' onClick={handleBack} disabled={currentStep === 0} className='mr-2 flex-1'>
						{L.Onboarding.Back()}
					</Button>
					<Button onClick={handleNext} className='ml-2 flex-1'>
						{isLastStep ? L.Onboarding.Complete() : L.Onboarding.Next()}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	)
}

export const openOnboardingDrawer = new BehaviorSubject<boolean>(false)

export const OnboardingDrawer = () => {
	const onboardingComplete = useGetSubject(onboardingCompleteSubject)

	// Auto-open onboarding once if it has not been completed yet
	useEffect(() => {
		if (onboardingComplete === false) {
			openOnboardingDrawer.next(true)
		}
	}, [])

	const isOnboardingOpen = useGetSubject(openOnboardingDrawer)

	return <OnboardingDrawerComponent open={isOnboardingOpen} onOpenChange={open => openOnboardingDrawer.next(open)} />
}
